import { Component, Prop, h, Host } from '@stencil/core';

const COMPONENT_NAME = 'sn-hero-header-type-1';

/**
 * Hero header section for SignNow brand main landing pages.
 * Two-column layout: content (heading, feature list, form) on the left,
 * illustration on the right. All content is inline-editable via pure slot pass-through
 * into the inner `hero-header-type-1` component.
 *
 * @product signnow
 * @source intro
 * @version 2.0.0
 * @category SignNow
 * @slot heading - {required}, <me-heading variant="1" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Main hero heading
 * @slot description - <me-list listType="icon"> - Feature list with icons
 * @slot action-primary - {required}, <form-free-trial-type-1> - Primary action form
 * @slot media - {required}, <me-picture lazy="false" media="(min-width: 768px)"> - Hero illustration. The `media` prop loads a 1x1 pixel spacer on mobile (below 768px) so the heavy image is only fetched on desktop
 * @example
 *   <sn-hero-header-type-1>
 *     <me-heading slot="heading" variant="1" tag="h1">Take your eSignature workflows to the next level</me-heading>
 *     <me-list slot="description" list-type="icon" layout="inline" icon-inline="true" items='[{"text":"Quick to start","icon":"arrows--chevron-down-circle"},{"text":"Easy-to-use","icon":"arrows--chevron-down-circle"},{"text":"24/7 support","icon":"arrows--chevron-down-circle"}]'></me-list>
 *     <form-free-trial-type-1 slot="action-primary" button-variant="primary" mobile-layout="stacked" note="No credit card required" submit-text="Start your free trial"></form-free-trial-type-1>
 *     <me-picture slot="media" media="(min-width: 768px)" src-set="https://marketing-static.signnow.com/355/images/e-signature/group-frame-intro.png 1x, https://marketing-static.signnow.com/355/images/e-signature/group-frame-intro@2x.png 2x" webp-src-set="https://marketing-static.signnow.com/355/images/e-signature/group-frame-intro.webp 1x, https://marketing-static.signnow.com/355/images/e-signature/group-frame-intro@2x.webp 2x" aspect-ratio="1.2" lazy="false" alt="E-signature frame illustration"></me-picture>
 *   </sn-hero-header-type-1>
 */
@Component({
  tag: 'sn-hero-header-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNHeroHeaderType1 {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  render() {
    return (
      <Host class={COMPONENT_NAME} data-color-mode={this.colorMode}>
        <header role="banner">
          <sn-hero-header contentPosition="right">
            <slot name="heading"></slot>
            <slot name="description"></slot>
            <div slot="media" class="sn-hero-header-type-1__media">
              <slot name="media"></slot>
            </div>
            <slot name="action-primary"></slot>
          </sn-hero-header>
        </header>
      </Host>
    );
  }
}
